import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Moon, Sparkles, TrendingUp, Crown, Briefcase, Loader2, Star, RefreshCw, Volume2, Share2 } from "lucide-react";
import {
  castChart,
  fetchChart,
  fetchCelestial,
  fetchFortune,
  fetchWisdom,
  expandTradition,
  fetchAncestors,
} from "@/lib/api";
import { playBlessing } from "@/lib/blessing";
import { generateChartBlob } from "@/lib/chartCard";
import { CITIES } from "@/data/cities";
import { TRADITIONS, DOMAINS } from "@/data/traditions";
import CelestialSky from "@/components/three/CelestialSky";
import LunarPrompt from "@/components/LunarPrompt";
import RitualJournal from "@/components/RitualJournal";
import AncestorAltar from "@/components/AncestorAltar";

const ease = [0.22, 1, 0.36, 1];

const DOMAIN_ICON = {
  financial: TrendingUp,
  governance: Crown,
  business: Briefcase,
};

/* ------------------------- Cast Your Chart form ------------------------ */
const CastChartForm = ({ onCast }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [cityName, setCityName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const city = CITIES.find((c) => c.name.toLowerCase() === cityName.trim().toLowerCase());
    if (!date || !time) return toast.error("Enter your birth date and exact time.");
    if (!city) return toast.error("Choose your birthplace from the list.");
    setBusy(true);
    try {
      const bc = await castChart({
        date,
        time,
        place: city.name,
        lat: city.lat,
        lon: city.lon,
        tz_offset: city.tz,
      });
      toast.success("Your chart is cast. The sky remembers your first breath.");
      onCast(bc);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The stars withheld their reading.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease }}
      className="glass mx-auto mt-12 max-w-xl rounded-3xl p-8 md:p-10"
      data-testid="cast-chart-form"
    >
      <p className="mb-6 text-sm font-light leading-relaxed text-white/55">
        Give the sky your origin point — the moment and place of your first breath — and the
        Council will read your natal chart against the living heavens.
      </p>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <label className="block">
          <span className="overline mb-2 block text-[0.6rem]">Birth date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none focus:border-[#C5A059]/70"
            data-testid="chart-date-input"
          />
        </label>
        <label className="block">
          <span className="overline mb-2 block text-[0.6rem]">Exact time</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none focus:border-[#C5A059]/70"
            data-testid="chart-time-input"
          />
        </label>
      </div>
      <label className="mt-5 block">
        <span className="overline mb-2 block text-[0.6rem]">Birthplace</span>
        <input
          list="sybil-cities"
          value={cityName}
          onChange={(e) => setCityName(e.target.value)}
          placeholder="Start typing a city…"
          className="w-full rounded-xl border border-[#C5A059]/25 bg-black/40 px-4 py-3 text-sm text-white/90 outline-none placeholder:text-white/25 focus:border-[#C5A059]/70"
          data-testid="chart-city-input"
        />
        <datalist id="sybil-cities">
          {CITIES.map((c) => (
            <option key={c.name} value={c.name} />
          ))}
        </datalist>
      </label>
      <button
        type="submit"
        disabled={busy}
        className="group mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border border-[#C5A059] bg-[#C5A059] px-8 py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#050505] transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        data-testid="cast-chart-submit"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {busy ? "Reading the heavens…" : "Cast Your Chart"}
      </button>
    </motion.form>
  );
};

/* --------------------------- Natal + sky panel ------------------------- */
const Placement = ({ label, value, glyph }) => (
  <div className="flex flex-col">
    <span className="overline text-[0.55rem]">{label}</span>
    <span className="mt-1 font-serif text-xl font-light text-[#C5A059]">
      {glyph ? `${glyph} ` : ""}
      {value}
    </span>
  </div>
);

const NatalPanel = ({ chart, sky }) => {
  const c = chart.chart;
  const asc = c.ascendant;
  const mp = sky?.moon_phase || c.moon_phase;
  return (
    <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
      <CelestialSky
        sky={sky}
        className="aspect-square w-full overflow-hidden rounded-3xl border border-[#C5A059]/15 bg-black/50"
      />
      <div>
        <p className="text-xs font-light text-white/45">
          Born {chart.date} · {chart.time} · {chart.place}
        </p>
        <div className="mt-6 grid grid-cols-3 gap-6">
          <Placement label="Sun" value={c.sun_sign} glyph={c.planets.Sun.glyph} />
          <Placement label="Moon" value={c.moon_sign} glyph={c.planets.Moon.glyph} />
          <Placement label="Rising" value={asc.sign} glyph={asc.glyph} />
        </div>
        <div className="mt-8 flex items-center gap-3 rounded-2xl border border-[#C5A059]/15 bg-black/40 px-5 py-4">
          <Moon size={20} className="text-[#C5A059]" />
          <div>
            <p className="text-sm font-light text-white/80" data-testid="sky-moon-phase">
              {mp.phase_name} · {mp.illumination}% lit
            </p>
            <p className="text-[0.7rem] uppercase tracking-[0.18em] text-white/40">
              Moon now in {sky?.moon_sign || c.moon_sign} · {mp.waxing ? "waxing" : "waning"}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {sky &&
            Object.entries(sky.planets).map(([name, p]) => (
              <span
                key={name}
                className="rounded-full border border-[#C5A059]/20 px-3 py-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-white/55"
              >
                {name} {p.glyph}
              </span>
            ))}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- Fortune section --------------------------- */
const DomainCard = ({ id, title, data, delay }) => {
  const Icon = DOMAIN_ICON[id];
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay, ease }}
      className="flex flex-col rounded-2xl border border-[#C5A059]/15 bg-[#070707] p-6"
      data-testid={`fortune-${id}`}
    >
      <div className="flex items-center gap-3 text-[#C5A059]">
        <Icon size={18} strokeWidth={1.5} />
        <h4 className="font-serif text-xl font-light">{title}</h4>
      </div>
      <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-white/60">{data?.reading}</p>
      <div className="mt-5 rounded-xl border border-[#C5A059]/15 bg-black/40 px-4 py-3">
        <p className="overline text-[0.5rem]">Best window</p>
        <p className="mt-1 text-xs font-light italic text-[#e8d3a6]">{data?.best_window}</p>
      </div>
    </motion.div>
  );
};

const FortunePanel = ({ fortune, loading, onConsult, hasChart, chart }) => {
  const [ancestors, setAncestors] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [autoKey, setAutoKey] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    fetchAncestors().then(setAncestors).catch(() => {});
  }, []);

  const shareReading = async () => {
    if (!chart || !fortune) return;
    setSharing(true);
    try {
      const blob = await generateChartBlob({ chart: chart, place: chart.place, fortune });
      const file = new File([blob], "sybils-chart.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My Sybils Chart & Fortune" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sybils-chart.png";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Your chart card is downloaded.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create the share card.");
    } finally {
      setSharing(false);
    }
  };

  const hear = async (id) => {
    setPlayingId(id);
    try {
      const audio = await playBlessing(id);
      audio.addEventListener("ended", () => setPlayingId((p) => (p === id ? null : p)));
    } catch (e) {
      setPlayingId(null);
      toast.error("The ancestor's voice is momentarily silent.");
    }
  };

  // Auto-play the first ancestor's blessing when a fresh governance reading appears.
  useEffect(() => {
    if (fortune && ancestors.length && autoKey !== fortune.headline) {
      setAutoKey(fortune.headline);
      hear(ancestors[0].id).catch(() => {});
    }
  }, [fortune, ancestors, autoKey]);

  return (
  <div className="mt-8">
    {!fortune ? (
      <div className="flex flex-col items-center rounded-3xl border border-dashed border-[#C5A059]/25 py-16 text-center">
        <Star size={28} strokeWidth={1} className="text-[#C5A059]/60" />
        <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-white/50">
          The Oracle will read today's transits against your natal chart and name the most
          favourable hours for money, power and enterprise.
        </p>
        <button
          onClick={() => onConsult(false)}
          disabled={loading || !hasChart}
          className="group mt-7 inline-flex items-center gap-3 rounded-full border border-[#C5A059] px-8 py-4 text-xs font-medium uppercase tracking-[0.24em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-50"
          data-testid="consult-oracle-btn"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? "Consulting the heavens…" : "Consult the Oracle"}
        </button>
      </div>
    ) : (
      <div data-testid="fortune-result">
        <div className="rounded-3xl border border-[#C5A059]/15 bg-gradient-to-b from-[#0b0912] to-[#070707] p-8 md:p-10">
          <div className="flex items-start justify-between gap-4">
            <p className="overline text-[0.6rem]">Today's Fortune</p>
            <div className="flex items-center gap-3">
              <button
                onClick={shareReading}
                disabled={sharing}
                className="flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.16em] text-white/40 transition-colors hover:text-[#C5A059]"
                title="Share your chart & fortune"
                data-testid="share-reading-btn"
              >
                {sharing ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />}
                Share
              </button>
              <button
                onClick={() => onConsult(true)}
                disabled={loading}
                className="text-white/30 transition-colors hover:text-[#C5A059]"
                title="Draw again"
                data-testid="refresh-fortune-btn"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              </button>
            </div>
          </div>
          <h3 className="mt-4 font-serif text-[clamp(1.5rem,3.5vw,2.4rem)] font-light italic leading-tight text-white">
            {fortune.headline}
          </h3>
          <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-white/65">
            {fortune.overall}
          </p>
          <div className="mt-6 flex items-center gap-3 rounded-2xl border border-[#5a4a1f]/40 bg-[#C5A059]/5 px-5 py-4">
            <Moon size={18} className="shrink-0 text-[#C5A059]" />
            <p className="text-sm font-light italic text-[#e8d3a6]">{fortune.lunar_counsel}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
          <DomainCard id="financial" title="Finance" data={fortune.financial} delay={0} />
          <DomainCard id="governance" title="Governance" data={fortune.governance} delay={0.08} />
          <DomainCard id="business" title="Enterprise" data={fortune.business} delay={0.16} />
        </div>
        {ancestors.length > 0 && (
          <div
            className="mt-6 rounded-2xl border border-[#C5A059]/15 bg-black/40 p-6"
            data-testid="governance-blessing"
          >
            <div className="flex items-center gap-3 text-[#C5A059]">
              <Volume2 size={16} />
              <p className="overline text-[0.55rem]">Your ancestors bless this governance</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {ancestors.map((a) => (
                <button
                  key={a.id}
                  onClick={() => hear(a.id)}
                  disabled={playingId === a.id}
                  className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/40 px-4 py-2 text-[0.65rem] font-light uppercase tracking-[0.16em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-60"
                  data-testid={`governance-voice-${a.id}`}
                >
                  {playingId === a.id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Volume2 size={13} />
                  )}
                  {a.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )}
  </div>
  );
};

/* -------------------------- Traditions library ------------------------- */
const TraditionsLibrary = () => {
  const [active, setActive] = useState(null);
  const [domain, setDomain] = useState(DOMAINS[0].id);
  const [expansion, setExpansion] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = (t) => {
    setActive(t);
    setDomain(DOMAINS[0].id);
    setExpansion(null);
  };

  const ask = async () => {
    if (!active) return;
    setBusy(true);
    setExpansion(null);
    try {
      const res = await expandTradition({ tradition: active.name, domain, intro: active.intro });
      setExpansion(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The archive is sealed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-16" data-testid="traditions-library">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {TRADITIONS.map((t, i) => (
          <motion.button
            key={t.id}
            onClick={() => open(t)}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.05, ease }}
            className={`group rounded-2xl border p-5 text-left transition-colors duration-300 ${
              active?.id === t.id
                ? "border-[#C5A059] bg-[#C5A059]/10"
                : "border-[#C5A059]/15 bg-[#070707] hover:border-[#C5A059]/50"
            }`}
            data-testid={`tradition-card-${t.id}`}
          >
            <h4 className="font-serif text-lg font-light text-[#C5A059]">{t.name}</h4>
            <p className="mt-1 text-[0.65rem] uppercase tracking-[0.14em] text-white/40">{t.region}</p>
            <p className="mt-3 line-clamp-3 text-xs font-light leading-relaxed text-white/50">
              {t.intro}
            </p>
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {active && (
          <motion.div
            key={active.id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5, ease }}
            className="overflow-hidden"
          >
            <div className="glass mt-6 rounded-3xl p-8 md:p-10" data-testid="tradition-detail">
              <p className="overline text-[0.6rem]">{active.figures}</p>
              <h3 className="mt-3 font-serif text-3xl font-light text-white">{active.name}</h3>
              <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-white/60">
                {active.intro}
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                {DOMAINS.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => {
                      setDomain(d.id);
                      setExpansion(null);
                    }}
                    className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.16em] transition-colors ${
                      domain === d.id
                        ? "border-[#C5A059] bg-[#C5A059] text-[#050505]"
                        : "border-[#C5A059]/30 text-white/60 hover:border-[#C5A059]/70"
                    }`}
                    data-testid={`domain-${d.label.toLowerCase()}`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <button
                onClick={ask}
                disabled={busy}
                className="group mt-7 inline-flex items-center gap-3 rounded-full border border-[#C5A059] px-8 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-[#C5A059] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#050505] disabled:opacity-50"
                data-testid="expand-tradition-btn"
              >
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                {busy ? "The oracle deliberates…" : "Ask the Oracle deeper"}
              </button>

              <AnimatePresence>
                {expansion && (
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease }}
                    className="mt-7 whitespace-pre-line rounded-2xl border border-[#C5A059]/15 bg-black/40 p-6 text-sm font-light leading-relaxed text-white/70"
                    data-testid="tradition-expansion"
                  >
                    {expansion.text}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ----------------------------- Root section ---------------------------- */
export const CelestialCouncil = () => {
  const [chart, setChart] = useState(undefined); // undefined=loading, null=none, obj=cast
  const [sky, setSky] = useState(null);
  const [wisdom, setWisdom] = useState(null);
  const [fortune, setFortune] = useState(null);
  const [loadingFortune, setLoadingFortune] = useState(false);
  const [journalTick, setJournalTick] = useState(0);

  const hydrate = async () => {
    try {
      const [s, w] = await Promise.all([fetchCelestial(), fetchWisdom()]);
      setSky(s);
      setWisdom(w);
    } catch (e) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const bc = await fetchChart();
        setChart(bc || null);
        if (bc) hydrate();
      } catch (e) {
        setChart(null);
      }
    })();
  }, []);

  const onCast = (bc) => {
    setChart(bc);
    hydrate();
  };

  const consult = async (refresh) => {
    setLoadingFortune(true);
    try {
      setFortune(await fetchFortune(refresh));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "The Oracle is veiled. Try again shortly.");
    } finally {
      setLoadingFortune(false);
    }
  };

  return (
    <section id="celestial" className="py-28" data-testid="celestial-council">
      <p className="overline mb-6">The Celestial Council — Sky &amp; Ancestral Timing</p>
      <h2 className="mb-6 font-serif text-[clamp(2rem,5vw,4rem)] font-light tracking-tight text-white">
        Read the heavens for your <span className="italic text-[#C5A059]">next move</span>.
      </h2>
      <p className="max-w-2xl text-sm font-light leading-relaxed text-white/55">
        Real lunar position and planetary transits, read against your birth chart, and translated
        through ancient matriarchal wisdom into counsel for finance, governance and enterprise.
      </p>

      {/* Daily wisdom */}
      {wisdom && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="mt-10 rounded-3xl border border-[#C5A059]/15 bg-gradient-to-r from-[#0b0912] to-[#070707] p-8"
          data-testid="daily-wisdom"
        >
          <div className="flex items-center gap-3">
            <Sparkles size={16} className="text-[#C5A059]" />
            <p className="overline text-[0.6rem]">
              Wisdom of the Day · {wisdom.tradition}
            </p>
          </div>
          <p className="mt-4 max-w-3xl font-serif text-xl font-light italic leading-relaxed text-white/80 md:text-2xl">
            “{wisdom.text}”
          </p>
        </motion.div>
      )}

      {/* Lunar ritual prompt */}
      <LunarPrompt onComplete={() => setJournalTick((t) => t + 1)} />

      {/* Lunar diary of completed rituals */}
      <RitualJournal refreshKey={journalTick} />

      {chart === undefined && (
        <p className="mt-12 font-serif text-lg font-light italic text-[#C5A059] animate-pulse">
          The Council gathers…
        </p>
      )}

      {chart === null && <CastChartForm onCast={onCast} />}

      {chart && (
        <>
          <div className="mt-14">
            <NatalPanel chart={chart} sky={sky} />
          </div>
          <FortunePanel
            fortune={fortune}
            loading={loadingFortune}
            onConsult={consult}
            hasChart={!!chart}
            chart={chart}
          />
        </>
      )}

      {/* Ancestor Altar */}
      <AncestorAltar />

      {/* Wisdom traditions library */}
      <div className="mt-24">
        <p className="overline mb-6">The Matriarch Codices — Ancient Traditions</p>
        <h3 className="font-serif text-[clamp(1.6rem,4vw,3rem)] font-light tracking-tight text-white">
          Eight lineages of <span className="italic text-[#C5A059]">feminine power</span>.
        </h3>
        <p className="mt-4 max-w-2xl text-sm font-light leading-relaxed text-white/55">
          Curated intros to each tradition — ask the Oracle to expand any one into deeper teaching
          for finance, governance or enterprise.
        </p>
        <TraditionsLibrary />
      </div>
    </section>
  );
};

export default CelestialCouncil;
